/**
 * テレビ向けデモ用モック（API 不通時のフォールバック）
 */

import { buildTwoHourSlots, rawToCongestionTier, dailyAvgCongestion } from '../utils/tvCongestionDisplay';
import {
  buildIntroPanelData,
  buildStationTodayPanel,
  buildStationTomorrowPanel,
  buildTodayOverviewPanel,
  buildYesterdayOverviewPanel,
  buildActionScreenData,
  buildTodaySummaryText,
  buildDateTimeMatrixFromCalendar,
  toIsoDate,
  addDaysIso,
} from '../utils/tvDemoDataTransform';
import {
  buildWeeklyLocationHeadline,
  buildWeeklyUnifiedSummary,
  buildWeeklyWebGuidePanel,
  formatWeeklyPeriodLabel,
  formatGeneratedAtLabel,
  getWeeklyPeriodRange,
} from '../utils/tvWeeklyDemoTransform';
import { hourlyMapToWeeklyDayStatus } from '../utils/tvWeeklyStatus';
import { getTvSpotConfig, TV_SPOT_LOCATION_OPTIONS } from './tvSpotLocation';
import { TV_BROADCAST_DEFAULT_PERIOD, TV_BROADCAST_PERIODS } from './tvBroadcastPeriod';
import { TV_STATION_ID } from './tvDemoDisplay';

const today = new Date();
const targetIso = toIsoDate(today);
const WEEKDAY_JA = ['日', '月', '火', '水', '木', '金', '土'];

const addDays = (base, n) => {
  const d = new Date(base);
  d.setDate(d.getDate() + n);
  return d;
};

const mockHourly = (seed, bump = 0) => {
  const map = {};
  for (let h = 9; h <= 19; h += 1) {
    const afternoon = h >= 12 && h <= 15 ? 6 : 0;
    map[h] = Math.min(20, Math.max(4, 6 + seed + afternoon + bump + Math.floor((h - 9) / 3)));
  }
  return map;
};

const todayMap = mockHourly(0, 3);
const tomorrowMap = mockHourly(0, 1);
const dayAfterMap = mockHourly(0, 0);

const laterDays = [3, 4, 5, 6].map((offset) => {
  const d = addDays(today, offset);
  const w = WEEKDAY_JA[d.getDay()];
  const map = mockHourly(0, offset % 2);
  const tier = rawToCongestionTier(dailyAvgCongestion(map));
  return {
    iso: `mock-${offset}`,
    dateLabel: `${w}${d.getMonth() + 1}/${d.getDate()}`,
    slots: buildTwoHourSlots(map),
    ...tier,
  };
});

const mockPeak = {
  highlightArea: '本町4丁目商店街',
  peakLabel: '13〜15時ピーク',
};

const mockStationPeak = {
  highlightArea: '駅前',
  peakLabel: '13〜14時ピーク',
};

const buildMockTomorrowPanel = (spotConfig) =>
  buildStationTomorrowPanel({
    calendarData: [],
    detailData: null,
    baseIso: targetIso,
    locationName: spotConfig.label,
    locationId: spotConfig.id,
    imageSrc: spotConfig.imageSrc,
    panelTitle: `${spotConfig.label} · 明日の人通り`,
  });

const buildMockSpotPanels = (broadcastPeriod) =>
  Object.fromEntries(
    TV_SPOT_LOCATION_OPTIONS.map(({ id }) => {
      const spotConfig = getTvSpotConfig(id);
      return [
        id,
        {
          today: buildStationTodayPanel({
            hourlyMap: todayMap,
            locationName: spotConfig.label,
            locationId: spotConfig.id,
            imageSrc: spotConfig.imageSrc,
            period: broadcastPeriod,
          }),
          tomorrow: buildMockTomorrowPanel(spotConfig),
          week: {
            locationId: spotConfig.id,
            locationName: spotConfig.label,
            imageSrc: spotConfig.imageSrc,
            panelTitle: `${spotConfig.label} · 1週間の流れ`,
            today: { label: '今日', slots: buildTwoHourSlots(todayMap) },
            tomorrow: { label: '明日', slots: buildTwoHourSlots(tomorrowMap) },
            dayAfter: { label: '明後日', slots: buildTwoHourSlots(dayAfterMap) },
            laterDays,
            summary: '週末に向けて人通りが増える見込み',
          },
        },
      ];
    }),
  );

/** デモ用：日平均がそのまま 少ない(≤7)/通常(≤12)/多い(≥13) になる hourly */
const mockHourlyAtAvg = (avg) => {
  const map = {};
  for (let h = 9; h <= 19; h += 1) {
    map[h] = avg;
  }
  return map;
};

/**
 * 日曜始まりのデモパターン（少ない / 通常 / 多い が必ず混ざる）
 * index: 0=日 … 6=土
 */
const MOCK_WEEKLY_AVG = {
  station: {
    previous: [10, 9, 8, 10, 11, 16, 17],
    next: [16, 10, 9, 10, 5, 11, 17],
  },
  'old-town': {
    previous: [16, 10, 5, 14, 10, 11, 17],
    next: [17, 9, 8, 15, 10, 6, 16],
  },
};

const buildMockWeeklyLocation = (spotId, periodStart, dataKind, kind) => {
  const spot = getTvSpotConfig(spotId);
  const avgs = MOCK_WEEKLY_AVG[spotId]?.[kind] || MOCK_WEEKLY_AVG.station.next;
  const days = [];
  for (let i = 0; i < 7; i += 1) {
    const iso = addDaysIso(periodStart, i);
    const dt = new Date(`${iso}T12:00:00`);
    const status = hourlyMapToWeeklyDayStatus(mockHourlyAtAvg(avgs[i]));
    days.push({
      date: iso,
      weekday: WEEKDAY_JA[dt.getDay()],
      value: status.value,
      tier: status.tier,
      statusLabel: status.statusLabel,
      dataKind,
    });
  }
  return {
    spotId,
    locationName: spot.label,
    days,
    headline: buildWeeklyLocationHeadline(days, dataKind),
    insufficientData: false,
  };
};

const buildMockWeeklyWeekBlock = (baseIso, kind, generatedAtLabel) => {
  const dataKind = kind === 'previous' ? 'actual' : 'forecast';
  const { periodStart, periodEnd } = getWeeklyPeriodRange(baseIso, kind);
  const locations = TV_SPOT_LOCATION_OPTIONS.map(({ id }) =>
    buildMockWeeklyLocation(id, periodStart, dataKind, kind),
  );
  return {
    periodStart,
    periodEnd,
    periodLabel: formatWeeklyPeriodLabel(periodStart, periodEnd),
    generatedAtLabel,
    dataKind,
    kindLabel: dataKind === 'actual' ? '計測結果' : 'AIによる予測',
    panelTitle: dataKind === 'actual' ? '先週の人通り' : '今週の人通り予報',
    locations,
    insufficientData: false,
    summary: buildWeeklyUnifiedSummary(locations, dataKind),
    imageSrc: getTvSpotConfig('station').imageSrc,
  };
};

export const buildMockWeeklyTvDemoPayload = (baseIso = targetIso) => {
  const generatedAt = new Date();
  const generatedAtLabel = formatGeneratedAtLabel(generatedAt);
  const previousWeek = buildMockWeeklyWeekBlock(baseIso, 'previous', generatedAtLabel);
  const nextWeek = buildMockWeeklyWeekBlock(baseIso, 'next', generatedAtLabel);
  const coverageLabel = `対象：${TV_SPOT_LOCATION_OPTIONS.map((o) => o.label).join('・')}`;
  const focusHeadline = nextWeek.locations[1]?.headline || nextWeek.locations[0]?.headline;
  const intro = {
    channel: '飛騨高山ケーブルネットワーク 12ch',
    title: '高山の人通り予報',
    periodStart: nextWeek.periodStart,
    periodEnd: nextWeek.periodEnd,
    periodLabel: nextWeek.periodLabel,
    coverageLabel,
    headline: focusHeadline?.startsWith('今週') || focusHeadline?.startsWith('データ')
      ? focusHeadline
      : `今週は、${focusHeadline}`,
    trialLabel: '実証中',
  };

  return {
    mode: 'weekly',
    generatedAt: generatedAt.toISOString(),
    generatedAtLabel,
    meta: {
      channel: intro.channel,
      segmentTitle: intro.title,
    },
    intro,
    previousWeek,
    nextWeek,
    webGuide: buildWeeklyWebGuidePanel({ intro, generatedAtLabel }),
    fetchedAt: 'mock',
    debug: { mock: true, mode: 'weekly' },
  };
};

export const buildMockTvDemoPayload = (broadcastPeriod = TV_BROADCAST_DEFAULT_PERIOD) => {
  const spots = buildMockSpotPanels(broadcastPeriod);
  const usePastTense = broadcastPeriod === TV_BROADCAST_PERIODS.EVENING;
  const focusTodaySummary = buildTodaySummaryText(todayMap, usePastTense);

  return {
    mode: 'daily',
    meta: {
      channel: '飛騨高山ケーブルネットワーク 12ch',
      segmentTitle: '高山市街 人通り予報',
    },
    intro: buildIntroPanelData({
      segmentTitle: '高山市街 人通り予報',
      targetIso,
      peak: mockPeak,
      busiestPart: '昼',
      focusHourlyMap: todayMap,
      period: broadcastPeriod,
      stationTodayCaption: spots[TV_STATION_ID].today.caption,
      todaySummary: focusTodaySummary,
    }),
    spots,
    todayOverview: buildTodayOverviewPanel({
      baseIso: targetIso,
      spotResults: {},
    }),
    yesterdayOverview: buildYesterdayOverviewPanel({
      baseIso: targetIso,
      spotResults: {},
    }),
    action: buildActionScreenData({
      targetIso,
      focusLocationName: '本町4丁目商店街',
      peak: mockStationPeak,
      weeklySummary: '週末にかけて午後の混雑が強まる見込み',
      weeklyMatrix: buildDateTimeMatrixFromCalendar([], targetIso, 7),
      period: broadcastPeriod,
      todaySummary: focusTodaySummary,
    }),
    fetchedAt: 'mock',
    debug: { mock: true, broadcastPeriod, mode: 'daily' },
  };
};

/** @deprecated buildMockTvDemoPayload を使用 */
export const tvDemoMockPayload = buildMockTvDemoPayload();

export { targetIso as mockTargetIso };
